import React, { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

export default function Registration() {
  const [form, setForm] = useState({
    vorname: "",
    name: "",
    adresse: "",
    telefon: "",
    email: "",
    termin: "",
    nachricht: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await api.post("/registrations", payload);
      setSubmitted(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Etwas ist schiefgelaufen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="anmeldung" className="py-24 sm:py-32 bg-[#FDFBF7]" data-testid="registration-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 lg:sticky lg:top-28"
          >
            <p className="eyebrow mb-6">Anmeldung</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2A332C] leading-[1.05] mb-8">
              Melde dich an — <br />
              <span className="font-display-italic text-[#A66B56]">ich melde mich zurück</span>.
            </h2>
            <p className="text-[#5C6B5E] text-lg leading-relaxed mb-8">
              Tragt eure Angaben und euren voraussichtlichen Geburtstermin ein. Ich melde mich innerhalb weniger Tage persönlich bei euch – ich freue mich darauf, euch kennenzulernen.
            </p>
            <div className="rounded-[2rem] overflow-hidden h-64">
              <img
                src="https://customer-assets.emergentagent.com/job_postpartum-care-7/artifacts/czy866lp_1000100811.jpg"
                alt="Michele in einem Rapsfeld"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-7"
          >
            {submitted ? (
              <div
                className="bg-[#F4EFE6] rounded-[2rem] p-10 sm:p-14 text-center"
                data-testid="registration-success"
              >
                <CheckCircle2 className="w-16 h-16 text-[#5C6B5E] mx-auto mb-6" strokeWidth={1.3} />
                <h3 className="font-display text-4xl text-[#2A332C] mb-4">Vielen Dank!</h3>
                <p className="text-[#5C6B5E] text-lg leading-relaxed max-w-md mx-auto">
                  Deine Anmeldung ist bei mir eingegangen. Ich melde mich in den nächsten Tagen persönlich bei dir.
                  Alles Gute bis dahin!
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-[#F4EFE6] rounded-[2rem] p-8 sm:p-12 space-y-5 border border-white/40"
                data-testid="registration-form"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="eyebrow block mb-2">Vorname</label>
                    <input
                      className="input-elegant"
                      name="vorname"
                      value={form.vorname}
                      onChange={handleChange}
                      required
                      data-testid="reg-vorname"
                    />
                  </div>
                  <div>
                    <label className="eyebrow block mb-2">Name</label>
                    <input
                      className="input-elegant"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      data-testid="reg-name"
                    />
                  </div>
                </div>
                <div>
                  <label className="eyebrow block mb-2">Adresse</label>
                  <input
                    className="input-elegant"
                    name="adresse"
                    placeholder="Strasse, PLZ Ort"
                    value={form.adresse}
                    onChange={handleChange}
                    required
                    data-testid="reg-adresse"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="eyebrow block mb-2">Telefon</label>
                    <input
                      className="input-elegant"
                      name="telefon"
                      type="tel"
                      value={form.telefon}
                      onChange={handleChange}
                      required
                      data-testid="reg-telefon"
                    />
                  </div>
                  <div>
                    <label className="eyebrow block mb-2">E-Mail (optional)</label>
                    <input
                      className="input-elegant"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      data-testid="reg-email"
                    />
                  </div>
                </div>
                <div>
                  <label className="eyebrow block mb-2">Voraussichtlicher Geburtstermin</label>
                  <input
                    className="input-elegant"
                    name="termin"
                    type="date"
                    value={form.termin}
                    onChange={handleChange}
                    required
                    data-testid="reg-termin"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2">Nachricht (optional)</label>
                  <textarea
                    className="input-elegant"
                    style={{ minHeight: 120, resize: "vertical" }}
                    name="nachricht"
                    value={form.nachricht}
                    onChange={handleChange}
                    placeholder="Gibt es etwas, das ich schon im Voraus wissen sollte?"
                    data-testid="reg-nachricht"
                  />
                </div>
                {error && (
                  <p className="text-sm text-red-700 bg-red-50 p-3 rounded-xl" data-testid="reg-error">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="reg-submit"
                >
                  {loading ? "Wird gesendet..." : "Anmelden"}
                </button>
                <p className="text-xs text-[#5C6B5E] text-center">
                  Deine Daten werden vertraulich behandelt und nicht an Dritte weitergegeben.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
